import { ChangeDetectorRef, Component, HostListener, ViewChild } from '@angular/core';
import { OffcanvasOptions } from '../../../../core/_base/metronic';
import { FileBrowserService } from '../../../../core/file-browser/file-browser.service';
import { FacilityService } from '../../../../core/configuration/_services/facility.service';
import { finalize, last } from 'rxjs/operators';
import { LayoutUtilsService } from '../../../../core/_base/crud';
import { HttpEventType } from '@angular/common/http';
import { SystemService } from '../../../../core/configuration/_services/system.service';
import { UnitService } from '../../../../core/configuration/_services/unit.service';
import { ChatbotUiService } from '../../../../core/chatbot/chatbot-ui.service';
import { environment } from '../../../../../environments/environment';
import { UserService } from '../../../../core/auth/_services/user.service';
import * as JSZip from 'jszip';

@Component({
  selector: 'kt-file-browser',
  templateUrl: './file-browser.component.html',
  styleUrls: ['./file-browser.component.scss']
})
export class FileBrowserComponent {
  @ViewChild('ktOffCanvasRef', { static: true }) ktOffCanvasElement;
  constructor(
    private readonly fileBrowserService: FileBrowserService,
    public _changeDetectorRef: ChangeDetectorRef,
    private readonly facilityService: FacilityService,
    private readonly systemService: SystemService,
    private readonly unitService: UnitService,
    private readonly layoutUtilsService: LayoutUtilsService,
    private readonly chatbotService: ChatbotUiService,
    private readonly userService: UserService
  ) {
    this.isVmDeployment = environment.runtime.CLOUD_SERVICE_PROVIDER === 'vm';
  }

  offcanvasOptions: OffcanvasOptions = {
    overlay: true,
    baseClass: 'kt-quick-panel',
    closeBy: 'customer_form_close_btn',
    toggleBy: 'configuration_panel_toggler_btn'
  };
  isLoading: boolean = false;
  acceptedFileTypes: string = ".jpg, .jpeg, .png, .gif, .txt, .doc, .docx, .csv, .xls, .xlsx, .ppt, .pptx, .pdf" + (environment.runtime.CLOUD_SERVICE_PROVIDER !== 'vm' ? ", .zip" : ""); // Zip files allowed only for Azure/AWS deployments
  isSelectedFolderOpened: boolean = false;
  isLoadingFolderStructure: boolean = false;
  isVmDeployment: boolean;

  @HostListener('window:beforeunload', ['$event'])
  unloadNotification($event: any) {
    if (this.isFileUploading) {
      // Cancel the event
      $event.preventDefault();
      // Chrome requires returnValue to be set
      $event.returnValue = `File upload in progress, if you reload then it won't be uploaded. Please wait.`;
    }
  }

  get currentSelectedFolderPathInfo() {
    return this.fileBrowserService.currentSelectedFolderPathInfo;
  }

  set currentSelectedFolderPathInfo(data: any) {
    this.fileBrowserService.currentSelectedFolderPathInfo = data
  }

  get flatTransformedData() {
    return this.fileBrowserService.flatTransformedData;
  }

  get prevSelectedFileFolder() {
    return this.fileBrowserService.prevSelectedFileFolder;
  }
  set prevSelectedFileFolder(data: any) {
    this.fileBrowserService.prevSelectedFileFolder = data;
  }

  get isFileUploading() {
    return this.fileBrowserService.isFileUploading;
  }

  set isFileUploading(value: boolean) {
    this.fileBrowserService.isFileUploading = value;
  }

  set folderName(value: string) {
    this.fileBrowserService.folderName = value;
  }

  set duplicateFolderNamePresent(value: boolean) {
    this.fileBrowserService.duplicateFolderNamePresent = value;
  }

  isCurrentSelectionOpened(event) {
    this.isSelectedFolderOpened = event
  }

  toggleFileBrowser() {
    this.ktOffCanvasElement.offcanvas.show()
    if(this.flatTransformedData && this.flatTransformedData.length){
      return
    }
    else{
      this.isLoading = true;
      this.fileBrowserService.getSystemUnitFacilityList().subscribe({
        next: () => {
          if (this.flatTransformedData && this.flatTransformedData.length) {
            return
          }
          else {
            this.fileBrowserService.initFileBrowserData().subscribe(response => {
              this.fileBrowserService.prevSelectedFileFolder = null;
              this.currentSelectedFolderPathInfo = {
                path: '',
                index: null
              };
              this.fileBrowserService.transformObjectDataIntoFlatTree(response.data)
              this.isLoading = false;
              this._changeDetectorRef.detectChanges();
            });
          }
        },
        error: err => {
          console.error(err);
        }
      })
    }
  }

  close = () => {
    this.ktOffCanvasElement.offcanvas.hide()
  }

  handleFileUpload(event: any) {
    //Only one file upload at a time is supported. If user selects another file inbetween the previous file upload will cancel.
    this.fileBrowserService.getFilesAndFolders(this.currentSelectedFolderPathInfo.path).subscribe(response => {
      this.fileBrowserService.transformObjectDataIntoFlatTree(response.data, this.currentSelectedFolderPathInfo.index);
      this.flatTransformedData[this.currentSelectedFolderPathInfo.index].isOpened = true;
      this._changeDetectorRef.detectChanges();
      let file: File[] = event.target.files
      this.isFileUploading = true;

      // Check if file is a zip file
      const isZipFile = file[0].type === 'application/zip' || file[0].type === 'application/x-zip-compressed' || file[0].name.toLowerCase().endsWith('.zip');

      let newDataObjectCreated = {
        "name": file[0]?.name,
        "type": "file",
        "incompleteFile": true,
        "progress": 0,
        "currentFolder": { ...this.currentSelectedFolderPathInfo },
      }
      this.handleDuplicateName(newDataObjectCreated);
      this.deleteDuplicateTemporaryUpload(newDataObjectCreated.name);
      const uploadedFileLevel = this.flatTransformedData[this.currentSelectedFolderPathInfo.index].level + 1;
      newDataObjectCreated["level"] = uploadedFileLevel;

      // Only add to UI if not a zip file
      if (!isZipFile) {
        this.flatTransformedData.splice(this.currentSelectedFolderPathInfo.index + 1, 0, newDataObjectCreated);
      } else {
        this.layoutUtilsService.showActionNotification('Uploading file...');
      }

      // Upload zip files to temp folder, other files to selected folder
      const uploadDestination = isZipFile ? environment.runtime.TEMP_FOLDER_PATH + '/' : this.currentSelectedFolderPathInfo.path;

      this.fileBrowserService.uploadFileFromFileBrowser(file[0], newDataObjectCreated.name, uploadDestination, 2).pipe(
        finalize(() => {
          this.isFileUploading = false;
        })
      ).subscribe(event => {
        if (event.type === HttpEventType.UploadProgress) {
          // Only update progress for non-zip files (zip files don't show progress in UI)
          if (!isZipFile) {
            const percentDone = Math.round((100 * event.loaded) / event.total);
            newDataObjectCreated['progress'] = percentDone;
            this._changeDetectorRef.detectChanges();
          }
        }
        else if (event.type === HttpEventType.Response) {
          if (isZipFile) {
            // For zip files: show upload complete message and call extraction API
            this.handleZipFileExtraction(uploadDestination, newDataObjectCreated);
          } else {
            // Handle the response after the upload is complete.
            this.layoutUtilsService.showActionNotification(`${newDataObjectCreated['name']} got uploaded successfully.`);

            // Log file activity, temporary implementation for Azure only. Can be extended for AWS later.
            // if (environment.runtime.CLOUD_SERVICE_PROVIDER === 'azure') {
              let fileURL = event.fileUrl ? event.fileUrl.split('?')[0] : (uploadDestination + newDataObjectCreated.name);
              this.fileBrowserService.logFileActivity({
                fileURL: fileURL,
                action: 'CREATE',
                fileSize: file[0].size,
                fileType: file[0].type,
                status: 2,
              }).subscribe(
                (response) => {
                  this.chatbotService.uploadFiles([{ _id: response.data._id }]).subscribe();
                  this.fileBrowserService.getFilesAndFolders(newDataObjectCreated.currentFolder.path).subscribe(response => {
                    this.makeTemporaryItemAsPermanent(response.data, newDataObjectCreated);
                    this.isFileUploading = false;
                    this._changeDetectorRef.detectChanges();
                  });
                },
                (error) => {
                  console.error('Error logging file activity:', error);
                }
              );
            }
          // }
        }
      },
        error => {
          this.isFileUploading = false;
          newDataObjectCreated['uploadFailed'] = true;
          this._changeDetectorRef.detectChanges();
          if (error.status === 413) this.layoutUtilsService.showActionNotification(`${file[0].name} failed to upload due to file size limit.`);
          else this.layoutUtilsService.showActionNotification(`${file[0].name} failed to upload.`);
        }
      );
    });
  }

  private handleZipFileExtraction(uploadDestination: string, newDataObjectCreated: any) {
    const zipFilePath = uploadDestination + newDataObjectCreated.name;
    const currentPath = newDataObjectCreated.currentFolder.path;

    this.fileBrowserService.extractZipFile(zipFilePath, currentPath).subscribe(
      extractResponse => {
        if (extractResponse.status === 200) {
          this.layoutUtilsService.showActionNotification(extractResponse.message);
        } else {
          this.layoutUtilsService.showActionNotification('Error while extracting file contents: ' + extractResponse.message);
        }
      },
      extractError => {
        this.layoutUtilsService.showActionNotification('Error while extracting file contents: ' + extractError.message);
      }
    );
  }

  makeTemporaryItemAsPermanent(data: any, tempRecord: any) {
    for (const key of Object.keys(data)) {
      if (this.flatTransformedData.some(ele => ele['currentPath'] === key)) {
        delete data[key];
      }
    }
    for (const key of Object.keys(data)) {
      const newDataObject = {
        name: tempRecord.name,
        level: tempRecord.level,
        type: tempRecord.type,
        currentPath: key,
        url: data[key].url,
        lastModified: data[key].lastModified,
      };

      Object.keys(tempRecord).forEach(tempKey => delete tempRecord[tempKey]);
      Object.assign(tempRecord, newDataObject);
    }

  }

  deleteDuplicateTemporaryUpload(name: string) {
    const uploadedFileLevel = this.flatTransformedData[this.currentSelectedFolderPathInfo.index].level + 1;
    for(let i = this.currentSelectedFolderPathInfo.index+1; i < this.flatTransformedData.length;){
      if(this.flatTransformedData[i].level < uploadedFileLevel) break;
      else if(this.flatTransformedData[i].level === uploadedFileLevel && name === this.flatTransformedData[i].name && this.flatTransformedData[i].uploadFailed){
        this.flatTransformedData.splice(i, 1);
      }
      else i++;
    }
  }

  handleDuplicateName(temporaryFileObject: any) {
    const uploadedFileLevel = this.flatTransformedData[this.currentSelectedFolderPathInfo.index].level + 1;
    let counter = 0;
    let duplicateNameFound = false;
    let {name: newFileName, extension: newFileExtension} = this.fileBrowserService.getFileNameAndExtension(temporaryFileObject.name);
    for(let i = this.currentSelectedFolderPathInfo.index+1; i < this.flatTransformedData.length;){
      let {name: currentFileName, extension: currentFileExtension} = this.fileBrowserService.getFileNameAndExtension(this.flatTransformedData[i].name);
      if(this.flatTransformedData[i].level < uploadedFileLevel) break;
      else if(this.flatTransformedData[i].level === uploadedFileLevel && `${newFileName}${duplicateNameFound ? '('+counter+')': ''}` == currentFileName && newFileExtension == currentFileExtension){
        duplicateNameFound = true;
        if(this.flatTransformedData[i].uploadFailed) break;
        counter++;
        i = this.currentSelectedFolderPathInfo.index+1;
      }
      else i++;
    }
    newFileName = `${newFileName}${counter ? '('+counter+')' : ''}`;
    temporaryFileObject.name = newFileName.concat(`.${newFileExtension}`);
  }

  createNewFolder() {
    this.fileBrowserService.getFilesAndFolders(this.currentSelectedFolderPathInfo.path).subscribe(response => {
      this.fileBrowserService.transformObjectDataIntoFlatTree(response.data, this.currentSelectedFolderPathInfo.index);
      this.flatTransformedData[this.currentSelectedFolderPathInfo.index].isOpened = true;
      this._changeDetectorRef.detectChanges();
      this.folderName = '';
      this.duplicateFolderNamePresent = false;
      const newFolderLevel = this.flatTransformedData[this.currentSelectedFolderPathInfo.index].level + 1;
      let newDataObjectCreated = {
        "name": "temp1",
        "type": "folder",
        "currentPath": this.currentSelectedFolderPathInfo.path + 'temp1/',
        "isOpened": false,
        "level": newFolderLevel,
        "isTemporary": true,
        "isSelected": true
      }
      this.flatTransformedData.splice(this.currentSelectedFolderPathInfo.index + 1, 0, newDataObjectCreated)
      this.prevSelectedFileFolder.isSelected = false;
      this.prevSelectedFileFolder = this.flatTransformedData[this.currentSelectedFolderPathInfo.index]
      this.currentSelectedFolderPathInfo = { path: newDataObjectCreated.currentPath, index: this.currentSelectedFolderPathInfo.index + 1 };
      this._changeDetectorRef.detectChanges();
    })
  }

  get disableAction() {
    return this.currentSelectedFolderPathInfo.index === null || this.flatTransformedData[this.currentSelectedFolderPathInfo.index].isTemporary || !this.isAnyFileFolderSelected();
  }

  get showDownloadHierarchyButton() {
    // Hide button for AWS and on-premise deployments as feature is not implemented
    // return false
    return !this.isVmDeployment;
  }

  isAnyFileFolderSelected() {
    return !!this.flatTransformedData.find(data => data.isSelected)
  }

  extractFolderPaths(structure: any, currentPath: string = '') {
    const paths: string[] = [];

    for (const key in structure) {
      if (structure[key].type === 'folder') {
        const folderPath = currentPath ? `${currentPath}/${key}` : key;
        paths.push(folderPath);

        // Recursively extract children
        if (structure[key].children && Object.keys(structure[key].children).length > 0) {
          const childPaths = this.extractFolderPaths(structure[key].children, folderPath);
          paths.push(...childPaths);
        }
      }
    }

    return paths;
  }

  downloadFolderStructure() {
    this.isLoadingFolderStructure = true;
    this.layoutUtilsService.showActionNotification('Fetching folder structure...');

    // Fetch complete folder hierarchy from backend
    this.fileBrowserService.getFolderStructureForFileBrowser().subscribe(
      (response) => {
        try {
          const nestedStructure = response.data || {};

          // Convert nested structure to flat paths
          const allPaths = this.extractFolderPaths(nestedStructure);

          if (allPaths.length === 0) {
            this.layoutUtilsService.showActionNotification('No folders found in the file browser');
            this.isLoadingFolderStructure = false;
            return;
          }

          this.layoutUtilsService.showActionNotification('Creating folder structure ZIP...');
          this.createAndDownloadZip(allPaths);

          this.layoutUtilsService.showActionNotification(`Folder structure downloaded successfully (${allPaths.length} folders)`);
        } catch (error) {
          this.layoutUtilsService.showActionNotification('Failed to create folder structure: ' + error.message);
          console.error('Create folder structure error:', error);
        } finally {
          this.isLoadingFolderStructure = false;
        }
      },
      (error) => {
        this.layoutUtilsService.showActionNotification('Failed to fetch folder structure: ' + error.message);
        console.error('Fetch folder structure error:', error);
        this.isLoadingFolderStructure = false;
      }
    );
  }

  createAndDownloadZip(allPaths: string[]) {
    const zip = this.createZipWithFolderStructure(allPaths);

    // Generate and download ZIP file
    zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      this.downloadZipFile(zipBlob, `folder_structure_${timestamp}.zip`);
    });
  }

  createZipWithFolderStructure(allPaths: string[]) {
    // Create ZIP with folder structure using existing JSZip import
    const zip = new JSZip();

    // Convert MongoDB IDs to readable names for each folder path
    for (const folderPath of allPaths) {
      const segments = folderPath.split('/').filter(Boolean);
      const renamedSegments = segments.map(segment => {
        if (this.fileBrowserService.isMongoId(segment)) {
          const name = this.fileBrowserService.getName(segment);
          if (name) {
            // Determine entity type
            let entityType = 'unknown';
            if (this.fileBrowserService.facilityMap.has(segment)) {
              entityType = 'facility';
            } else if (this.fileBrowserService.unitMap.has(segment)) {
              entityType = 'unit';
            } else if (this.fileBrowserService.systemMap.has(segment)) {
              entityType = 'system';
            }

            return `${name}_${entityType}_${segment}`;
          }
        }
        return segment;
      });

      const renamedPath = renamedSegments.join('/');
      // Create empty folder in ZIP
      zip.folder(renamedPath);
    }

    return zip;
  }

  downloadZipFile(zipBlob: Blob, fileName: string) {
    const downloadLink = document.createElement('a');
    const url = URL.createObjectURL(zipBlob);

    downloadLink.href = url;
    downloadLink.download = fileName;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
    URL.revokeObjectURL(url);
  }
}
